import React, { useState, useMemo, useCallback } from 'react';
import { getCountryCallingCode } from 'react-phone-number-input';
import { useIntl } from 'react-intl';

import BaseIcon from 'common/BaseIcon';

import CountryLabel from './CountryLabel';
import CountryOption from './CountryOption';
import CountrySearch from './CountrySearch';
import Dropdown from '../../Dropdown/Dropdown';
import { DropdownList, OptionsList } from '../../Dropdown/UI';

const countryFilter =
  (searchValue) =>
    ({ label, value }) => {
      if (!searchValue) return true;

      const formatted = searchValue.replace('+', '').toLowerCase();
      const prefix = getCountryCallingCode(value);
      const searchableFields = [label, String(prefix)];

      return searchableFields.some((field) =>
        field?.toLowerCase().includes(formatted));
    };

const CountrySelect = ({ value, options, onChange }) => {
  const intl = useIntl();
  const inputRef = React.useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  const filteredOptions = useMemo(
    () => options.filter(countryFilter(searchValue)),
    [options, searchValue]
  );

  const handleOpenChange = useCallback((newValue) => {
    setIsOpen(newValue);
    if (newValue) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  const handleOnSearch = React.useCallback(({ target }) => {
    setSearchValue(target.value.trim());
  }, []);

  const handleSelect = useCallback((countryCode) => {
    onChange(countryCode);
    setIsOpen(false);
    setSearchValue('');
  }, [onChange]);

  return (
    <Dropdown
      buttonContent={
        <>
          <CountryLabel countryCode={value} />
          <BaseIcon icon="arrow-down" />
        </>
      }
      dropdownContent={
        <DropdownList>
          <CountrySearch
            ref={inputRef}
            value={searchValue}
            onChange={handleOnSearch}
            placeholder={intl.formatMessage({
              id: 'web.searchCountry',
              defaultMessage: 'Search by prefix or country',
            })}
          />

          <OptionsList>
            {filteredOptions.map(option => (
              <CountryOption
                key={option.value}
                countryCode={option.value}
                label={option.label}
                isSelected={option.value === value}
                onSelect={handleSelect}
              />
            ))}
          </OptionsList>
        </DropdownList>
      }
      open={isOpen}
      setOpen={handleOpenChange}
    />
  );
};

export default CountrySelect;
